import axios from 'axios';
import axiosInstance from '../utils/axiosInstance';
import tokenStorage from '../utils/tokenStorage';
import { API_CONFIG } from '../config/api.config';
import {
  DeviceLimitReachedError,
  isDeviceLimitReachedPayload,
} from '../utils/apiErrors';

/**
 * Shared in-flight refresh promise.
 *
 * Kept at module level (not on the authService object) so the function can be
 * called as a standalone export — e.g. `const { refreshAccessToken } = await
 * import('../services/authService')` from the axios interceptor. Object methods
 * that rely on `this` break when destructured/called standalone.
 */
let refreshPromise = null;

/**
 * Refresh the access token using the httpOnly refresh-token cookie.
 *
 * The backend rotates (invalidates) the refresh token on every refresh, so two
 * parallel calls would make the second one fail — and that failure can trigger
 * the backend's reuse detection, which revokes the whole session. Sharing a
 * single in-flight promise therefore prevents logging the user out.
 *
 * @returns {Promise<string>} New access token
 */
export const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      // Use a plain axios call (not axiosInstance) to avoid the response
      // interceptor, which would trigger a hard redirect on 401.
      const response = await axios.post(
        `${API_CONFIG.baseURL}/api/auth/refresh`,
        {},
        { withCredentials: true }
      );

      // Backend vraća token u ApiResponse formatu:
      // { success: true, data: { accessToken: "..." } }
      // Podržavamo i direktan format { accessToken: "..." } radi sigurnosti.
      const accessToken =
        response.data?.data?.accessToken || response.data?.accessToken;

      if (accessToken) {
        tokenStorage.set(accessToken);
        return accessToken;
      }

      throw new Error('No access token received');
    } catch (error) {
      // If refresh fails, clear token and throw error
      tokenStorage.clear();
      throw error;
    } finally {
      // Always release the shared promise so future refreshes can run.
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Auth Service
 * Handles authentication operations including login, logout, token refresh,
 * and token storage management
 */
const authService = {
  /**
   * Login user with username and password
   * @param {string} username - User username
   * @param {string} password - User password
   * @param {Object} [options] - Additional login options
   * @param {string} [options.deviceId] - Native installation id (never sent on web)
   * @returns {Promise<Object>} Response with user data and access token
   */
  async login(username, password, options = {}) {
    const payload = { username, password };

    // deviceId je opcioni deo LoginDto-a i šalje se samo sa native platforme.
    // Web login ostaje potpuno isti: { username, password }.
    if (options?.deviceId) {
      payload.deviceId = options.deviceId;
    }

    try {
      const response = await axiosInstance.post('/api/auth/login', payload);

      // Backend može vratiti token u različitim formatima
      // Format 1: { accessToken: "token" }
      // Format 2: { data: { accessToken: "token" } }
      // Format 3: { success: true, data: { accessToken: "token" } }
      
      let accessToken = response.data.accessToken || 
                       response.data.data?.accessToken ||
                       response.data.token;

      // Store access token in localStorage
      if (accessToken) {
        this.setAccessToken(accessToken);
      }

      return response.data;
    } catch (error) {
      // Handle specific error cases
      if (error.response) {
        const { status, data } = error.response;

        // Backend može odbiti prijavu zbog limita uređaja (409 DeviceLimitReached).
        // Tada korisnik mora da vidi konkretnu poruku, ne generički "Login failed".
        if (isDeviceLimitReachedPayload(status, data)) {
          throw new DeviceLimitReachedError(undefined, { cause: error });
        }

        switch (status) {
          case 401:
            throw new Error(data.message || 'Neispravni kredencijali');
          case 403:
            throw new Error(data.message || 'Lozinka nije postavljena');
          case 423:
            // ✅ Account temporarily locked after too many failed attempts.
            throw new Error(data.message || 'Nalog je privremeno zaključan. Pokušajte ponovo kasnije.');
          case 429:
            // ✅ Rate limited (too many requests).
            throw new Error(data.message || 'Previše pokušaja. Sačekajte minut i pokušajte ponovo.');
          case 400:
            throw new Error(data.message || 'Greška u validaciji');
          case 500:
            throw new Error('Greška na serveru. Pokušajte ponovo kasnije.');
          default:
            throw new Error(data.message || 'Prijavljivanje nije uspelo');
        }
      }
      
      throw new Error('Greška u mreži. Proverite vašu internet konekciju.');
    }
  },

  /**
   * Set password for a user who hasn't set one yet (hasSetHisPassword = false)
   * @param {string} newPassword - New password
   * @param {string} confirmNewPassword - Confirmation of new password
   * @returns {Promise<Object>} Response data
   */
  async setPassword(newPassword, confirmNewPassword) {
    try {
      const response = await axiosInstance.post('/api/auth/set-password', {
        newPassword,
        confirmNewPassword,
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data.message || 'Greška u validaciji lozinke');
          case 401:
            throw new Error(data.message || 'Neispravni kredencijali');
          case 500:
            throw new Error('Greška na serveru. Pokušajte ponovo kasnije.');
          default:
            throw new Error(data.message || 'Postavljanje lozinke nije uspelo');
        }
      }
      throw new Error('Greška u mreži. Proverite vašu internet konekciju.');
    }
  },

  /**
   * Logout user and revoke refresh token
   * Clears access token and calls backend to remove refresh token cookie
   */
  async logout() {
    try {
      // Call revoke endpoint to clear httpOnly cookie
      await axiosInstance.post('/api/auth/revoke', {});
    } catch (error) {
      // Even if revoke fails, we should clear local storage
      console.error('Logout error:', error);
    } finally {
      // Always clear access token from localStorage
      this.clearAccessToken();
    }
  },

  /**
   * Refresh access token using httpOnly refresh token cookie
   * @returns {Promise<string>} New access token
   */
  refresh() {
    // Delegate to the standalone function so the axios interceptor can call the
    // same implementation safely (shared in-flight promise lives at module level).
    return refreshAccessToken();
  },

  /**
   * Get access token from memory
   * @returns {string|null} Access token or null
   */
  getAccessToken() {
    return tokenStorage.get();
  },

  /**
   * Store access token in memory
   * @param {string} token - Access token to store
   */
  setAccessToken(token) {
    tokenStorage.set(token);
  },

  /**
   * Remove access token from memory
   */
  clearAccessToken() {
    tokenStorage.clear();
  },

  /**
   * Check if user is authenticated (has valid access token)
   * Note: This only checks if token exists, not if it's valid
   * @returns {boolean} True if token exists
   */
  isAuthenticated() {
    return !!this.getAccessToken();
  },

  /**
   * Check whether a JWT access token is expired (or about to expire).
   * @param {string} token - JWT token to check
   * @param {number} [skewSeconds=30] - Treat tokens expiring within this many seconds as expired
   * @returns {boolean} True if the token is missing, malformed or expired
   */
  isTokenExpired(token, skewSeconds = 30) {
    if (!token) return true;

    const payload = this.decodeToken(token);
    if (!payload || typeof payload.exp !== 'number') {
      // If we cannot determine the expiry, treat it as expired so that
      // the silent refresh flow gets a chance to run.
      return true;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return payload.exp <= nowInSeconds + skewSeconds;
  },

  /**
   * Decode JWT token to get user information
   * @param {string} token - JWT token to decode
   * @returns {Object|null} Decoded token payload or null
   */
  decodeToken(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  },

  /**
   * Get current user from access token
   * @returns {Object|null} User object or null
   */
  getCurrentUser() {
    const token = this.getAccessToken();
    
    if (!token) {
      return null;
    }

    return this.decodeToken(token);
  },

  /**
   * Get user role from JWT token
   * @param {Object} [user] - Already decoded token payload (avoids re-decoding the JWT)
   * @returns {string|null} User role (administrator, nurse, caretaker) or null
   */
  getUserRole(user = null) {
    const payload = user || this.getCurrentUser();

    if (!payload) {
      return null;
    }

    // Role is stored in Microsoft schema claim
    const role = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    return role ? role.toLowerCase() : null;
  },
};

export default authService;
